import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail, MessageSquare, Save } from 'lucide-react';
import type { OwnerAlertSettingsBody, OwnerAlertSettingsDto, OwnerAlertTestResultDto } from '@his/shared';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { dateTime } from '@/lib/utils';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { Alert, Button, Card, CardContent, CardDescription, CardHeader, CardTitle, Checkbox, Field, Input, Spinner, PasswordInput, Switch } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { TotpField, isValidTotp } from './OwnerConfirm';
import { useOwnerCapability } from './owner-nav';
import { TagListInput } from './TagListInput';

type Encryption = OwnerAlertSettingsDto['smtpEncryption'];

interface FormState {
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpFrom: string;
  smtpEncryption: Encryption;
  smsGatewayUrl: string;
  smsGatewayApiKey: string;
  smsSenderId: string;
  ownerEmails: string[];
  ownerPhones: string[];
  notifyCustomerAdmin: boolean;
  warnDaysBefore: string;
  silentAfterHours: string;
  paymentGraceDays: string;
  escrowRetentionDays: string;
  lifecycleSweepMode: 'report' | 'enforce';
}

const ENCRYPTION_OPTIONS: Array<{ value: Encryption; label: string }> = [
  { value: 'STARTTLS', label: 'STARTTLS (port 587)' },
  { value: 'SSL_TLS', label: 'SSL/TLS (port 465)' },
  { value: 'NONE', label: 'None (port 25)' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_PATTERN = /^\+?[0-9]{7,20}$/;

function fromDto(dto: OwnerAlertSettingsDto): FormState {
  return {
    smtpHost: dto.smtpHost ?? '',
    smtpPort: dto.smtpPort ? String(dto.smtpPort) : '',
    smtpUser: dto.smtpUser ?? '',
    smtpPassword: '',
    smtpFrom: dto.smtpFrom ?? '',
    smtpEncryption: dto.smtpEncryption,
    smsGatewayUrl: dto.smsGatewayUrl ?? '',
    smsGatewayApiKey: '',
    smsSenderId: dto.smsSenderId ?? '',
    ownerEmails: dto.ownerEmails,
    ownerPhones: dto.ownerPhones,
    notifyCustomerAdmin: dto.notifyCustomerAdmin,
    warnDaysBefore: String(dto.warnDaysBefore),
    silentAfterHours: String(dto.silentAfterHours),
    paymentGraceDays: String(dto.paymentGraceDays ?? 7),
    escrowRetentionDays: String(dto.escrowRetentionDays ?? 90),
    lifecycleSweepMode: dto.lifecycleSweepMode ?? 'report',
  };
}

const orNull = (value: string): string | null => value.trim() || null;

function toBody(form: FormState, clearPassword: boolean, clearApiKey: boolean): OwnerAlertSettingsBody {
  const port = Number.parseInt(form.smtpPort, 10);
  return {
    smtpHost: orNull(form.smtpHost),
    smtpPort: Number.isInteger(port) ? port : null,
    smtpUser: orNull(form.smtpUser),
    ...(clearPassword ? { smtpPassword: '' } : form.smtpPassword ? { smtpPassword: form.smtpPassword } : {}),
    smtpFrom: orNull(form.smtpFrom),
    smtpEncryption: form.smtpEncryption,
    smsGatewayUrl: orNull(form.smsGatewayUrl),
    ...(clearApiKey ? { smsGatewayApiKey: '' } : form.smsGatewayApiKey ? { smsGatewayApiKey: form.smsGatewayApiKey } : {}),
    smsSenderId: orNull(form.smsSenderId),
    ownerEmails: form.ownerEmails,
    ownerPhones: form.ownerPhones,
    notifyCustomerAdmin: form.notifyCustomerAdmin,
    warnDaysBefore: Number.parseInt(form.warnDaysBefore, 10),
    silentAfterHours: Number.parseInt(form.silentAfterHours, 10),
    paymentGraceDays: Number.parseInt(form.paymentGraceDays, 10),
    escrowRetentionDays: Number.parseInt(form.escrowRetentionDays, 10),
    lifecycleSweepMode: form.lifecycleSweepMode,
  };
}

function TestOutcome({ result }: { result: OwnerAlertTestResultDto | null }) {
  if (!result) return null;
  return (
    <p className={result.ok ? 'font-mono text-xs text-success' : 'font-mono text-xs text-destructive'}>
      {result.ok ? 'OK' : 'Failed'} — {result.detail}
    </p>
  );
}

/**
 * Global alert channels (plan §3.6, D7): SMTP, an HTTP SMS gateway,
 * recipients and thresholds. Secrets are write-only — the form shows only
 * whether one is stored, and "clear" sends an empty string.
 */
export function OwnerAlertSettingsForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const canSave = useOwnerCapability('alert_settings.update');
  const [form, setForm] = useState<FormState | null>(null);
  const [clearPassword, setClearPassword] = useState(false);
  const [clearApiKey, setClearApiKey] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [emailTest, setEmailTest] = useState<OwnerAlertTestResultDto | null>(null);
  const [smsTest, setSmsTest] = useState<OwnerAlertTestResultDto | null>(null);

  const settings = useQuery({ queryKey: ['owner-alert-settings'], queryFn: () => get<OwnerAlertSettingsDto>('/owner/alert-settings') });

  useEffect(() => {
    if (settings.data) {
      setForm(fromDto(settings.data));
      setClearPassword(false);
      setClearApiKey(false);
    }
  }, [settings.data]);

  const save = useMutation({
    mutationFn: () => put<OwnerAlertSettingsDto>('/owner/alert-settings', { ...toBody(form!, clearPassword, clearApiKey), totpCode }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['owner-alert-settings'] });
      setTotpCode('');
      toast.success('Alert settings saved');
    },
    onError: (error) => toast.error('Could not save alert settings', describeError(error)),
  });

  const testEmail = useMutation({
    mutationFn: () => post<OwnerAlertTestResultDto>('/owner/alert-settings/test-email'),
    onSuccess: setEmailTest,
    onError: (error) => setEmailTest({ ok: false, detail: describeError(error) }),
  });

  const testSms = useMutation({
    mutationFn: () => post<OwnerAlertTestResultDto>('/owner/alert-settings/test-sms'),
    onSuccess: setSmsTest,
    onError: (error) => setSmsTest({ ok: false, detail: describeError(error) }),
  });

  if (!form || !settings.data) {
    return (
      <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Spinner /> Loading settings…
      </div>
    );
  }
  const dto = settings.data;
  const update = (patch: Partial<FormState>) => setForm({ ...form, ...patch });
  const warn = Number.parseInt(form.warnDaysBefore, 10);
  const silent = Number.parseInt(form.silentAfterHours, 10);
  const grace = Number.parseInt(form.paymentGraceDays, 10);
  const retention = Number.parseInt(form.escrowRetentionDays, 10);
  const valid =
    Number.isInteger(warn) && warn >= 1 && warn <= 30 && Number.isInteger(silent) && silent >= 1 && silent <= 720 && Number.isInteger(grace) && grace >= 0 && grace <= 60 &&
    Number.isInteger(retention) && retention >= 7 && retention <= 3650;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <Mail className="h-4 w-4" /> Email (SMTP)
          </CardTitle>
          <CardDescription>
            Owner alerts go out here. It is also the platform mail server: a facility without its own SMTP sends through it, within the limits on the Facility email tab.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Host" htmlFor="smtp-host" className="sm:col-span-2">
            <Input id="smtp-host" value={form.smtpHost} onChange={(e) => update({ smtpHost: e.target.value })} placeholder="smtp.example.com" />
          </Field>
          <Field label="Port" htmlFor="smtp-port">
            <Input id="smtp-port" inputMode="numeric" value={form.smtpPort} onChange={(e) => update({ smtpPort: e.target.value.replace(/\D/g, '').slice(0, 5) })} />
          </Field>
          <Field label="Encryption" htmlFor="smtp-enc">
            <ChoiceButtons
              id="smtp-enc"
              layout="chips"
              value={form.smtpEncryption}
              onChange={(smtpEncryption) => update({ smtpEncryption })}
              options={ENCRYPTION_OPTIONS}
            />
          </Field>
          <Field label="User" htmlFor="smtp-user">
            <Input id="smtp-user" value={form.smtpUser} onChange={(e) => update({ smtpUser: e.target.value })} autoComplete="off" />
          </Field>
          <Field
            label="Password"
            htmlFor="smtp-pass"
            hint={dto.hasSmtpPassword ? (clearPassword ? 'Will be cleared on save.' : 'A password is stored. Leave blank to keep it.') : 'No password stored.'}
          >
            <div className="flex gap-2">
              <PasswordInput id="smtp-pass" autoComplete="new-password" value={form.smtpPassword} disabled={clearPassword} onChange={(e) => update({ smtpPassword: e.target.value })} />
              {dto.hasSmtpPassword && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setClearPassword(!clearPassword)}>
                  {clearPassword ? 'Keep' : 'Clear'}
                </Button>
              )}
            </div>
          </Field>
          <Field label="From address" htmlFor="smtp-from" className="sm:col-span-2">
            <Input id="smtp-from" value={form.smtpFrom} onChange={(e) => update({ smtpFrom: e.target.value })} placeholder="HIS alerts <alerts@example.com>" />
          </Field>
          <div className="space-y-1 sm:col-span-2">
            <Button type="button" variant="outline" size="sm" loading={testEmail.isPending} onClick={() => testEmail.mutate()}>
              Send test email
            </Button>
            <TestOutcome result={emailTest} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="h-4 w-4" /> SMS gateway
          </CardTitle>
          <CardDescription>
            An HTTP gateway. A URL with <code>{'{{to}}'}</code> and <code>{'{{message}}'}</code> is fetched with GET; otherwise a JSON body is POSTed. With no gateway saved, the API&apos;s own SMS_PROVIDER carries alerts.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <Field label="Gateway URL" htmlFor="sms-url" className="sm:col-span-2">
            <Input id="sms-url" value={form.smsGatewayUrl} onChange={(e) => update({ smsGatewayUrl: e.target.value })} placeholder="https://sms.example.com/send?to={{to}}&text={{message}}" />
          </Field>
          <Field
            label="API key"
            htmlFor="sms-key"
            hint={dto.hasSmsGatewayApiKey ? (clearApiKey ? 'Will be cleared on save.' : 'A key is stored. Leave blank to keep it.') : 'No key stored.'}
          >
            <div className="flex gap-2">
              <PasswordInput id="sms-key" autoComplete="new-password" value={form.smsGatewayApiKey} disabled={clearApiKey} onChange={(e) => update({ smsGatewayApiKey: e.target.value })} />
              {dto.hasSmsGatewayApiKey && (
                <Button type="button" variant="ghost" size="sm" onClick={() => setClearApiKey(!clearApiKey)}>
                  {clearApiKey ? 'Keep' : 'Clear'}
                </Button>
              )}
            </div>
          </Field>
          <Field label="Sender id" htmlFor="sms-sender">
            <Input id="sms-sender" value={form.smsSenderId} onChange={(e) => update({ smsSenderId: e.target.value })} />
          </Field>
          <div className="space-y-1 sm:col-span-2">
            <Button type="button" variant="outline" size="sm" loading={testSms.isPending} onClick={() => testSms.mutate()}>
              Send test SMS
            </Button>
            <TestOutcome result={smsTest} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Recipients</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <TagListInput id="owner-emails" label="Owner emails" values={form.ownerEmails} onChange={(v) => update({ ownerEmails: v })} validate={(v) => EMAIL_PATTERN.test(v)} placeholder="name@example.com" hint="Enter or comma to add." />
          <TagListInput id="owner-phones" label="Owner phone numbers" values={form.ownerPhones} onChange={(v) => update({ ownerPhones: v })} validate={(v) => PHONE_PATTERN.test(v)} placeholder="+92300…" hint="Digits with an optional leading +." />
          <Checkbox
            checked={form.notifyCustomerAdmin}
            onCheckedChange={(checked) => update({ notifyCustomerAdmin: checked })}
            label={<>Also notify the facility&apos;s admin</>}
            description="The contact person and Super Admins get the same email and SMS."
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Thresholds</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Warn this many days before the lock" htmlFor="warn-days" hint="Default 3: a 7-day grace period warns from day 4.">
            <Input id="warn-days" inputMode="numeric" value={form.warnDaysBefore} onChange={(e) => update({ warnDaysBefore: e.target.value.replace(/\D/g, '').slice(0, 2) })} />
          </Field>
          <Field label="Customer silent after (hours)" htmlFor="silent-hours" hint="A local server that has not checked in this long raises CUSTOMER_SILENT.">
            <Input id="silent-hours" inputMode="numeric" value={form.silentAfterHours} onChange={(e) => update({ silentAfterHours: e.target.value.replace(/\D/g, '').slice(0, 3) })} />
          </Field>
          <Field label="Payment grace (days)" htmlFor="payment-grace" hint="A facility that sent payment proof is not cut back to read-only for this many days while the payment is confirmed.">
            <Input id="payment-grace" inputMode="numeric" value={form.paymentGraceDays} onChange={(e) => update({ paymentGraceDays: e.target.value.replace(/\D/g, '').slice(0, 2) })} />
          </Field>
          <Field label="Keep purge escrow copies (days)" htmlFor="escrow-days" hint="How long the sealed copy of a purged facility is kept before it is deleted too. Default 90.">
            <Input id="escrow-days" inputMode="numeric" value={form.escrowRetentionDays} onChange={(e) => update({ escrowRetentionDays: e.target.value.replace(/\D/g, '').slice(0, 4) })} />
          </Field>
          <Switch
            checked={form.lifecycleSweepMode === 'enforce'}
            onCheckedChange={(checked) => update({ lifecycleSweepMode: checked ? 'enforce' : 'report' })}
            label="End subscriptions automatically"
            description="Off: the hourly check only reports which facilities would move to read-only. On: it moves them, at the end of the day in their own time zone, and sends the reminders 14, 7, 3 and 1 days before."
          />
          <p className="text-xs text-muted-foreground">Last saved {dateTime(dto.updatedAt)}</p>
        </CardContent>
      </Card>

      <div className="space-y-3 lg:col-span-2">
        {canSave ? (
          <>
            {!valid && <Alert variant="warning">Warn days must be 1–30, the silent threshold 1–720 hours and the payment grace 0–60 days.</Alert>}
            <TotpField value={totpCode} onChange={setTotpCode} />
            <Button className="gap-1.5" loading={save.isPending} disabled={!valid || !isValidTotp(totpCode)} onClick={() => save.mutate()}>
              <Save className="h-3.5 w-3.5" /> Save alert settings
            </Button>
          </>
        ) : (
          <Alert>Only the Owner can change the alert channels.</Alert>
        )}
      </div>
    </div>
  );
}
